import { useState } from "react";

import { useTranslation } from "react-i18next";

import { ReturnLabelSource } from "../../models/returns/IReturnsApi";
import classNames from "../../utils/classNames";
import ActionPanelFrame, { ReturnActionPanelProps } from "./ActionPanelFrame";
import LabelFileFields, { EMPTY_LABEL, LabelFields, labelFieldsComplete } from "./LabelFileFields";

/** Accept the return: eBay's label (charged to the eBay account) or one of our own. */
export default function AcceptPanel({ detail, onConfirm, onCancel, busy }: ReturnActionPanelProps) {
  const { t } = useTranslation("returns");
  const isEbay = detail.channel === "ebay";
  const [source, setSource] = useState<ReturnLabelSource>(isEbay ? "ebay" : "own");
  const [label, setLabel] = useState<LabelFields>(EMPTY_LABEL);
  const own = source === "own";

  const options: { source: ReturnLabelSource; title: string; sub: string }[] = [
    { source: "ebay", title: t("eBay provides the label"), sub: t("Charged to your eBay account. Buyer prints it from the return.") },
    { source: "own", title: t("Upload my own label"), sub: t("Carrier, tracking number and the label file.") },
  ];

  return (
    <ActionPanelFrame
      kind="accept"
      detail={detail}
      canConfirm={!own || labelFieldsComplete(label)}
      onConfirm={(message) => onConfirm(own ? { message, labelSource: "own", carrier: label.carrier.trim(), tracking: label.tracking.trim(), labelFileName: label.labelFileName, labelFileBase64: label.labelFileBase64 } : { message, labelSource: "ebay" })}
      onCancel={onCancel}
      busy={busy}
    >
      {isEbay && (
        <fieldset className="flex flex-col gap-1.5">
          <legend className="mb-1 text-[12px] font-semibold text-gray-700">{t("Return label")}</legend>
          {options.map((o) => (
            <label key={o.source} className={classNames("flex items-start gap-2 rounded-lg border px-2.5 py-2 text-[12px]", source === o.source ? "border-primary-600" : "border-gray-200")}>
              <input type="radio" name="label-source" checked={source === o.source} onChange={() => setSource(o.source)} className="mt-0.5 border-gray-300 text-primary-600 focus:ring-primary-500" />
              <span>
                <span className="font-semibold text-gray-900">{o.title}</span>
                <br />
                <span className="text-gray-500">{o.sub}</span>
              </span>
            </label>
          ))}
        </fieldset>
      )}
      {own && <LabelFileFields value={label} onChange={setLabel} disabled={busy} />}
    </ActionPanelFrame>
  );
}
